/**
 * Source: docs/rules/01-qualities.md and docs/rules/02-creation.md
 *
 * Most Qualities only affect play, so creation just records the selection.
 * The exceptions are marked: `requires` gates whether a Quality may be taken,
 * `grantsRank` mutates the character at creation, and `exemptFromLimit` lets a
 * Quality sit outside the three-Quality cap.
 */

import type { FactionId } from "@personnel/setting";

export const QUALITY_COST = 5;

/** Maximum Qualities, not counting any whose `exemptFromLimit` is true. */
export const MAX_QUALITIES = 3;

/** A captain may hold at most one Quality of each tag. */
export const QUALITY_TAGS = ["weaver", "physical"] as const;
export type QualityTag = (typeof QUALITY_TAGS)[number];

export interface QualityRequirement {
  /** Attribute the captain must have at or above `minimum`. */
  readonly attribute?: "rank";
  readonly minimum?: number;
  /** The captain must belong to this faction. */
  readonly faction?: FactionId;
  /** The captain must be at or above this Reputation Tier. */
  readonly reputationTier?: number;
  /**
   * Requirements the engine cannot yet evaluate, because the concept has no
   * rules document. Recorded so the UI can warn rather than silently allow.
   */
  readonly unmodelled?: string;
}

export interface QualityDef {
  readonly id: string;
  readonly name: string;
  readonly tag: QualityTag | null;
  readonly description: string;
  readonly requires?: readonly QualityRequirement[];
  /** Does not count against MAX_QUALITIES. */
  readonly exemptFromLimit?: boolean;
  /** Sets Rank to this value at creation, overriding the purchased rating. */
  readonly grantsRank?: number;
}

export const QUALITIES: readonly QualityDef[] = [
  {
    id: "aethercognitive",
    name: "Aethercognitive",
    tag: "weaver",
    description:
      "Maintains impeccable spatial awareness around the ship. May remove one Power Token from a Maneuver PAS after movement is complete if its associated Maneuver Points have not been spent. Cannot remove Power Tokens applied for ramming Avoidance Tests. Adds +2 to Avoidance Tests when being rammed.",
  },
  {
    id: "archon",
    name: "Archon",
    tag: "weaver",
    description:
      "A natural leader. Add +2 dice to Morale Tests for Ramming, or to Stress Reduction Tests when rallying a crew to abandon an ordered withdrawal or panicked retreat.",
  },
  {
    id: "celestial",
    name: "Celestial",
    tag: "physical",
    description:
      "Born of the interstellar void. When executing maneuvers the ship may move as if one size smaller, moving earlier than size order would normally allow. Adds one Stress to the Captain when used.",
  },
  {
    id: "cybersymbiot",
    name: "Cybersymbiot",
    tag: "weaver",
    description:
      "Keenly aware of the ship's mechanical workings. Add +2 dice to Damage Control dice pools.",
  },
  {
    id: "daredevil",
    name: "Daredevil",
    tag: null,
    description:
      "Pushes the ship beyond its structural limits. Exceeding the ship's Control Limit without auxiliary inertial compensators adds only half the Stress, rounded down. The ship still takes one Structure damage as normal.",
  },
  {
    id: "disciplinarian",
    name: "Disciplinarian",
    tag: null,
    description:
      "Runs the ship with strict discipline. Add +2 dice to Morale Test dice pools, but any failure that results in Surrender/Mutiny is automatically a mutiny result.",
  },
  {
    id: "distinguished-career",
    name: "Distinguished Career",
    tag: null,
    description:
      "A successful career has earned promotion to Rank 6. Most captains at this rank command one or more task forces rather than a single ship, using their attributes when present. May be taken as a fourth Quality.",
    requires: [{ attribute: "rank", minimum: 5 }],
    exemptFromLimit: true,
    grantsRank: 6,
  },
  {
    id: "drifter",
    name: "Drifter",
    tag: null,
    description:
      "Changes assignments often, and integrates quickly. Gains Familiarity with a command after two consecutive missions instead of three.",
  },
  {
    id: "engineer",
    name: "Engineer",
    tag: null,
    description:
      "Came to the captain's chair by way of the engineering bays. Reduce the difficulty of any Damage Control Test roll by one, to a minimum of one.",
  },
  {
    id: "helm-expert",
    name: "Helm Expert",
    tag: null,
    description:
      "May make two Sideslip maneuvers consecutively. After two consecutive Sideslips the ship must move forward at least one hex before executing another. MP costs are still paid as normal.",
  },
  {
    id: "inspirational",
    name: "Inspirational",
    tag: null,
    description:
      "Knows what to say to keep a crew calm. Add +2 dice to Stress Reduction Test dice pools.",
  },
  {
    id: "princep",
    name: "Princep",
    tag: null,
    description:
      "A Child of Callisto who speaks with the voice of the Imperatrix. When acting as group commander and bidding for control of neutral emplacements, the bid counts double, without paying double Cost. Usable on one emplacement per mission. If the Princep does not earn at least a Marginal Victory they earn no Experience for the mission and lose five Renown, which can cost a Reputation Tier.",
    requires: [
      { faction: "callistonian-empire" },
      { reputationTier: 4 },
    ],
  },
  {
    id: "stalwart",
    name: "Stalwart",
    tag: null,
    description:
      "Maintains calm aboard the ship even as it falls apart. Remove one Morale Test Token during the Recovery Phase; if this leaves none, no Morale Test is needed.",
  },
  {
    id: "tactician",
    name: "Tactician",
    tag: null,
    description:
      "Skilled at positioning. When designated group commander, may use the Tactical attribute in place of Rank when calculating Initiative Test results.",
  },
  {
    id: "terrestrial",
    name: "Terrestrial",
    tag: "physical",
    description:
      "A creature of dense gravitation. May operate the ship at higher than standard gravity, filling the first space on the Turn Track after a Standard or Aether Turn without moving the ship. Adds +2 Stress to the Captain each time it is used.",
  },
  {
    id: "well-connected",
    name: "Well-Connected",
    tag: null,
    description:
      "Resupplies with ease. When assigned as task force commander, add three dice to any Requisition Test dice pool.",
  },
];

const BY_ID = new Map(QUALITIES.map((quality) => [quality.id, quality]));

export function qualityById(id: string): QualityDef | undefined {
  return BY_ID.get(id);
}
